/**
 * WebSocket client for real-time document generation progress
 *
 * V3: Job-based tracking. Single WebSocket per job, receives status for all generations.
 * AutomaDoc Worker stores state in Redis for cross-pod consistency.
 */

/**
 * Types of events from the generation stream
 */
export type GenerationEventType = 'info' | 'warning' | 'error' | 'success' | 'progress' | 'complete'

/**
 * WebSocket event data structure
 */
export interface GenerationEvent {
  type: GenerationEventType
  recordId?: string
  message: string
  details?: {
    generationId?: string
    processed?: number
    total?: number
    filename?: string
    error?: string
    downloadUrl?: string
    failed?: number
    hadWarnings?: boolean
    warnings?: number
    warningMessages?: string[]
  }
  timestamp: number
}

/**
 * Individual generation status (from AutomaDoc Worker via CF Worker)
 */
export interface GenerationStatusInfo {
  generationId: string
  status: 'pending' | 'processing' | 'uploading' | 'completed' | 'failed' | 'cancelled'
  progress: string[]
  warnings: string[]
  currentStep?: string
  filename?: string
  downloadUrl?: string
  error?: string
  hadWarnings: boolean
}

/**
 * Job status response from WebSocket
 */
export interface WSJobStatusResponse {
  type: 'jobStatus'
  jobId: string
  found: boolean
  status: 'pending' | 'processing' | 'completed' | 'partial' | 'failed' | 'cancelled'
  totalRecords: number
  completedCount: number
  failedCount: number
  cancelledCount: number
  generations: GenerationStatusInfo[]
}

/**
 * Cancel response from WebSocket
 */
interface WSCancelResponse {
  type: 'cancelResult'
  jobId: string
  success: boolean
  message: string
  cancelledCount: number
}

/**
 * Info/Error message from server
 */
interface WSInfoMessage {
  type: 'info' | 'error'
  message: string
}

type WSResponse = WSJobStatusResponse | WSCancelResponse | WSInfoMessage

/**
 * Callbacks for handling job events
 */
export interface JobCallbacks {
  /** Called when job status is received (every poll) */
  onJobStatus?: (status: WSJobStatusResponse) => void
  /** Called for progress updates on any generation */
  onGenerationProgress?: (generationId: string, message: string) => void
  /** Called when a generation completes successfully */
  onGenerationComplete?: (generationId: string, filename?: string, downloadUrl?: string, hadWarnings?: boolean) => void
  /** Called when a generation fails */
  onGenerationError?: (generationId: string, error?: string) => void
  /** Called for warnings on any generation */
  onGenerationWarning?: (generationId: string, warning: string) => void
  /** Called when a generation is cancelled */
  onGenerationCancelled?: (generationId: string) => void
  /** Called when the entire job completes (all generations done) */
  onJobComplete?: (status: WSJobStatusResponse) => void
  /** Called when the job is cancelled */
  onJobCancelled?: (cancelledCount: number) => void
  /** Called when job is not found after retries */
  onJobNotFound?: () => void
  /** Called for connection info messages */
  onInfo?: (message: string) => void
}

/**
 * Connection options
 */
export interface ConnectionOptions {
  /** Polling interval in ms (default: 2000) */
  pollInterval?: number
  /** Max reconnection attempts (default: 10) */
  maxReconnectAttempts?: number
  /** Base delay between reconnection attempts in ms (default: 1000) */
  reconnectBaseDelay?: number
  /** Max delay between reconnection attempts in ms (default: 30000) */
  reconnectMaxDelay?: number
}

/**
 * Result of connecting to a job WebSocket
 */
export interface JobConnection {
  /** Close the WebSocket connection */
  close: () => void
  /** Cancel the job (all pending generations) */
  cancel: () => void
  /** Promise that resolves when job completes */
  promise: Promise<WSJobStatusResponse | null>
}

/**
 * Connect to a job WebSocket for real-time updates
 *
 * @param jobId - The job ID from the generate response
 * @param callbacks - Event callbacks
 * @param options - Connection options
 */
export function connectToJob(
  jobId: string,
  callbacks: JobCallbacks,
  options: ConnectionOptions = {}
): JobConnection {
  const {
    pollInterval = 2000,
    maxReconnectAttempts = 10,
    reconnectBaseDelay = 1000,
    reconnectMaxDelay = 30000,
  } = options

  const wsUrl = `${window.location.origin.replace(/^http/, 'ws')}/api/jobs/${jobId}/ws`

  let ws: WebSocket | null = null
  let isClosedManually = false
  let isCancelling = false
  let reconnectAttempts = 0
  let reconnectTimeout: ReturnType<typeof setTimeout> | null = null
  let pollTimeout: ReturnType<typeof setTimeout> | null = null
  let promiseResolve: ((value: WSJobStatusResponse | null) => void) | null = null
  let promiseReject: ((reason: Error) => void) | null = null
  let notFoundRetries = 0
  let lastJobStatus: WSJobStatusResponse | null = null
  const MAX_NOT_FOUND_RETRIES = 5

  // Track per-generation state for detecting new progress/warnings
  const generationState = new Map<string, {
    lastProgressLength: number
    lastWarningsLength: number
    lastStatus: GenerationStatusInfo['status'] | null
  }>()

  /**
   * Check if job status is terminal
   */
  function isTerminalStatus(status: WSJobStatusResponse['status']): boolean {
    return status === 'completed' || status === 'partial' || status === 'failed' || status === 'cancelled'
  }

  /**
   * Calculate reconnect delay with exponential backoff
   */
  function getReconnectDelay(): number {
    const delay = Math.min(
      reconnectBaseDelay * Math.pow(2, reconnectAttempts),
      reconnectMaxDelay
    )
    return delay * (0.8 + Math.random() * 0.4)
  }

  /**
   * Send a getJobStatus request
   */
  function requestStatus(): void {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'getJobStatus', jobId }))
    }
  }

  /**
   * Send a cancelJob request
   */
  function requestCancel(): void {
    if (ws && ws.readyState === WebSocket.OPEN) {
      isCancelling = true
      ws.send(JSON.stringify({ type: 'cancelJob', jobId }))
    }
  }

  /**
   * Schedule next poll
   */
  function scheduleNextPoll(): void {
    if (isClosedManually || isCancelling) return
    if (lastJobStatus && isTerminalStatus(lastJobStatus.status)) return

    pollTimeout = setTimeout(() => {
      requestStatus()
    }, pollInterval)
  }

  /**
   * Handle job status response
   */
  function handleJobStatus(response: WSJobStatusResponse): void {
    // Handle not found
    if (!response.found) {
      notFoundRetries++
      if (notFoundRetries <= MAX_NOT_FOUND_RETRIES) {
        console.log(`[WS Job] Job not found, retry ${notFoundRetries}/${MAX_NOT_FOUND_RETRIES}`)
        scheduleNextPoll()
        return
      }
      // Job truly doesn't exist
      callbacks.onJobNotFound?.()
      isClosedManually = true
      promiseReject?.(new Error('Job not found'))
      return
    }

    notFoundRetries = 0
    lastJobStatus = response

    // Notify job status
    callbacks.onJobStatus?.(response)

    // Process each generation's status
    for (const gen of response.generations) {
      // Initialize state if needed
      if (!generationState.has(gen.generationId)) {
        generationState.set(gen.generationId, {
          lastProgressLength: 0,
          lastWarningsLength: 0,
          lastStatus: null,
        })
      }

      const state = generationState.get(gen.generationId)!

      // Handle new progress messages
      if (gen.progress.length > state.lastProgressLength) {
        const newMessages = gen.progress.slice(state.lastProgressLength)
        state.lastProgressLength = gen.progress.length

        for (const message of newMessages) {
          callbacks.onGenerationProgress?.(gen.generationId, message)
        }
      }

      // Handle new warnings
      if (gen.warnings.length > state.lastWarningsLength) {
        const newWarnings = gen.warnings.slice(state.lastWarningsLength)
        state.lastWarningsLength = gen.warnings.length

        for (const warning of newWarnings) {
          callbacks.onGenerationWarning?.(gen.generationId, warning)
        }
      }

      // Handle status transitions
      if (gen.status !== state.lastStatus) {
        const previousStatus = state.lastStatus
        state.lastStatus = gen.status

        if (gen.status === 'completed' && previousStatus !== 'completed') {
          callbacks.onGenerationComplete?.(gen.generationId, gen.filename, gen.downloadUrl, gen.hadWarnings)
        } else if (gen.status === 'failed' && previousStatus !== 'failed') {
          callbacks.onGenerationError?.(gen.generationId, gen.error)
        } else if (gen.status === 'cancelled' && previousStatus !== 'cancelled') {
          callbacks.onGenerationCancelled?.(gen.generationId)
        }
      }
    }

    // Check if job is complete
    if (isTerminalStatus(response.status)) {
      callbacks.onJobComplete?.(response)
      isClosedManually = true
      promiseResolve?.(response)
      // Backend will close the connection
    } else {
      scheduleNextPoll()
    }
  }

  /**
   * Handle cancel response
   */
  function handleCancelResponse(response: WSCancelResponse): void {
    if (response.success) {
      callbacks.onInfo?.(`Cancelled ${response.cancelledCount} generation(s)`)
      callbacks.onJobCancelled?.(response.cancelledCount)
      isClosedManually = true
      promiseResolve?.(lastJobStatus)
    } else {
      callbacks.onInfo?.(`Cancel failed: ${response.message}`)
      isCancelling = false
      scheduleNextPoll()
    }
  }

  /**
   * Create and connect WebSocket
   */
  function connect(): void {
    ws = new WebSocket(wsUrl)

    ws.onopen = () => {
      console.log('[WS Job] Connected to job:', jobId)
      reconnectAttempts = 0
      requestStatus()
    }

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as WSResponse
        if (message.type === 'jobStatus') {
          handleJobStatus(message as WSJobStatusResponse)
        } else if (message.type === 'cancelResult') {
          handleCancelResponse(message as WSCancelResponse)
        } else if (message.type === 'info' || message.type === 'error') {
          callbacks.onInfo?.((message as WSInfoMessage).message)
        }
      } catch (e) {
        console.warn('[WS Job] Failed to parse message:', e)
      }
    }

    ws.onerror = (error) => {
      console.error('[WS Job] WebSocket error:', error)
    }

    ws.onclose = (event) => {
      console.log('[WS Job] Connection closed:', event.code, event.reason)

      if (pollTimeout) {
        clearTimeout(pollTimeout)
        pollTimeout = null
      }

      // If manually closed or job complete, don't reconnect
      if (isClosedManually || (lastJobStatus && isTerminalStatus(lastJobStatus.status))) {
        promiseResolve?.(lastJobStatus)
        return
      }

      // Attempt reconnection
      if (reconnectAttempts < maxReconnectAttempts) {
        const delay = getReconnectDelay()
        reconnectAttempts++

        console.log(`[WS Job] Reconnecting in ${Math.round(delay)}ms (attempt ${reconnectAttempts}/${maxReconnectAttempts})`)
        callbacks.onInfo?.(`Reconnecting... (attempt ${reconnectAttempts}/${maxReconnectAttempts})`)

        reconnectTimeout = setTimeout(() => {
          if (!isClosedManually) {
            connect()
          }
        }, delay)
      } else {
        promiseReject?.(new Error('Max reconnection attempts exceeded'))
      }
    }
  }

  const promise = new Promise<WSJobStatusResponse | null>((resolve, reject) => {
    promiseResolve = resolve
    promiseReject = reject
    connect()
  })

  return {
    close: () => {
      isClosedManually = true
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout)
        reconnectTimeout = null
      }
      if (pollTimeout) {
        clearTimeout(pollTimeout)
        pollTimeout = null
      }
      ws?.close()
    },
    cancel: () => {
      requestCancel()
    },
    promise,
  }
}

/**
 * Trigger download from a URL
 */
export function triggerDownload(url: string, filename?: string): void {
  const a = document.createElement('a')
  a.href = url
  if (filename) {
    a.download = filename
  }
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}
